/**
 * Accordion Component
 * Handles accordion behavior including:
 * - Smooth slide open/close animations
 * - Exclusive mode (close others when one opens)
 * - Keyboard navigation
 * - ARIA state management
 */

class AccordionItem {
	constructor(details, group) {
		this.details = details;
		this.group = group;
		this.summary = details.querySelector('summary');
		this.content = details.querySelector('.accordion-item__content-wrapper');
		this.isAnimating = false;
		this.animation = null;

		if (!this.summary || !this.content) return;

		this.init();
	}

	init() {
		// Prevent default toggle and handle it ourselves
		this.summary.addEventListener('click', (e) => {
			e.preventDefault();
			if (this.isAnimating) return;

			if (this.details.open) {
				this.close();
			} else {
				this.open();
			}
		});

		// Handle keyboard
		this.summary.addEventListener('keydown', (e) => {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				if (this.isAnimating) return;

				if (this.details.open) {
					this.close();
				} else {
					this.open();
				}
			}
		});
	}

	open() {
		// Store the position of this summary before any changes
		const summaryRect = this.summary.getBoundingClientRect();
		const summaryTopBefore = summaryRect.top;

		// If exclusive mode, close others first (without animation to get instant height change)
		if (this.group.exclusive) {
			this.group.closeOthersInstant(this);
		}

		// After closing others, check if we need to scroll to maintain position
		const summaryTopAfter = this.summary.getBoundingClientRect().top;
		const scrollDiff = summaryTopAfter - summaryTopBefore;

		if (Math.abs(scrollDiff) > 1) {
			// Adjust scroll position to keep summary in same place
			window.scrollBy(0, scrollDiff);
		}

		// Set open attribute to show content
		this.details.open = true;
		this.summary.setAttribute('aria-expanded', 'true');

		// Get the height
		const startHeight = 0;
		const endHeight = this.content.scrollHeight;

		// Cancel any running animation
		if (this.animation) {
			this.animation.cancel();
		}

		// Animate
		this.isAnimating = true;
		this.animation = this.content.animate(
			[
				{ height: `${startHeight}px` },
				{ height: `${endHeight}px` }
			],
			{
				duration: 400,
				easing: 'ease-out'
			}
		);

		this.animation.onfinish = () => {
			this.content.style.height = '';
			this.isAnimating = false;
			this.animation = null;
		};

		this.animation.oncancel = () => {
			this.isAnimating = false;
			this.animation = null;
		};
	}

	close() {
		// Get current height
		const startHeight = this.content.scrollHeight;

		// Cancel any running animation
		if (this.animation) {
			this.animation.cancel();
		}

		// Animate
		this.isAnimating = true;
		this.summary.setAttribute('aria-expanded', 'false');

		this.animation = this.content.animate(
			[
				{ height: `${startHeight}px` },
				{ height: '0px' }
			],
			{
				duration: 300,
				easing: 'ease-out'
			}
		);

		this.animation.onfinish = () => {
			this.details.open = false;
			this.content.style.height = '';
			this.isAnimating = false;
			this.animation = null;
		};

		this.animation.oncancel = () => {
			this.isAnimating = false;
			this.animation = null;
		};
	}
}

class AccordionGroup {
	constructor(element) {
		this.container = element;
		this.exclusive = element.dataset.exclusive === 'true';
		this.items = [];

		const detailsElements = element.querySelectorAll('details');
		if (detailsElements.length === 0) return;

		detailsElements.forEach((details) => {
			this.items.push(new AccordionItem(details, this));
		});

		// Keyboard navigation between items
		this.initKeyboardNav();
	}

	closeOthers(currentItem) {
		this.items.forEach((item) => {
			if (item !== currentItem && item.details.open) {
				item.close();
			}
		});
	}

	closeOthersInstant(currentItem) {
		// Close others instantly (no animation) to allow proper scroll adjustment
		this.items.forEach((item) => {
			if (item !== currentItem && item.details.open) {
				// Cancel any running animation
				if (item.animation) {
					item.animation.cancel();
				}
				item.details.open = false;
				item.summary.setAttribute('aria-expanded', 'false');
				item.content.style.height = '';
				item.isAnimating = false;
				item.animation = null;
			}
		});
	}

	initKeyboardNav() {
		const summaries = this.container.querySelectorAll('summary');

		summaries.forEach((summary) => {
			summary.addEventListener('keydown', (e) => {
				const summaryArray = Array.from(summaries);
				const currentIndex = summaryArray.indexOf(summary);
				let newIndex;

				switch (e.key) {
					case 'ArrowDown':
						e.preventDefault();
						newIndex = currentIndex + 1;
						if (newIndex >= summaryArray.length) newIndex = 0;
						summaryArray[newIndex].focus();
						break;
					case 'ArrowUp':
						e.preventDefault();
						newIndex = currentIndex - 1;
						if (newIndex < 0) newIndex = summaryArray.length - 1;
						summaryArray[newIndex].focus();
						break;
					case 'Home':
						e.preventDefault();
						summaryArray[0].focus();
						break;
					case 'End':
						e.preventDefault();
						summaryArray[summaryArray.length - 1].focus();
						break;
				}
			});
		});
	}
}

// Initialize all accordion groups on page load
function initAccordions() {
	const accordionGroups = document.querySelectorAll('[data-accordion-group]');
	accordionGroups.forEach((group) => {
		// Skip if already initialized
		if (group.dataset.initialized) return;
		group.dataset.initialized = 'true';
		new AccordionGroup(group);
	});
}

// Initialize on DOMContentLoaded
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initAccordions);
} else {
	initAccordions();
}

// Re-initialize when Shopify section is loaded/updated (for theme editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
	document.addEventListener('shopify:section:load', (event) => {
		const section = event.target;
		const accordionGroups = section.querySelectorAll('[data-accordion-group]');
		accordionGroups.forEach((group) => {
			group.dataset.initialized = '';
			new AccordionGroup(group);
		});
	});
}
